// components/DegreeAudit/Progress/RequirementProgressBar.tsx

import { memo } from 'react';
import { useCreditUnit } from '../../../contexts/InstitutionConfigContext';

export interface RequirementProgressData {
  is_credit_based?: boolean;
  credits_completed?: number;
  credits_in_progress?: number;
  credits_planned?: number;
  credits_suggested?: number;
  credits_required?: number;
  completed_count?: number;
  completed?: number;
  in_progress_count?: number;
  planned_count?: number;
  suggested_count?: number;
  required?: number;
  // W27: override-aware effective requirement-completion (0–100). Present only on
  // nodes whose subtree contains an active override. The raw counts below stay
  // honest; when this is set the bar renders the effective fill (incl. override).
  effective_progress_percentage?: number;
  [key: string]: unknown;
}

export interface RequirementProgressBarProps {
  requirement: RequirementProgressData | null | undefined;
  showText?: boolean;
}

/**
 * Engagement-based progress tracking for individual requirements.
 * Shows how many courses are completed, in progress, planned, and suggested.
 */
const RequirementProgressBar = memo(
  ({ requirement, showText = true }: RequirementProgressBarProps) => {
    const creditUnit = useCreditUnit();
    // Credit-based choose blocks (e.g. "choose 26 credits from") use credit
    // counts instead of course counts for progress tracking.
    const isCreditBased = requirement?.is_credit_based;

    const completedCount = isCreditBased
      ? requirement?.credits_completed || 0
      : requirement?.completed_count || requirement?.completed || 0;
    const inProgressCount = isCreditBased
      ? requirement?.credits_in_progress || 0
      : requirement?.in_progress_count || 0;
    const plannedCount = isCreditBased
      ? requirement?.credits_planned || 0
      : requirement?.planned_count || 0;
    const suggestedCount = isCreditBased
      ? requirement?.credits_suggested || 0
      : requirement?.suggested_count || 0;
    const requiredCount = isCreditBased
      ? requirement?.credits_required || 1
      : requirement?.required || 1;

    const unit = isCreditBased ? creditUnit.abbrev : '';

    const engagedCount = completedCount + inProgressCount + plannedCount + suggestedCount;
    const engagementProgress = Math.min((engagedCount / requiredCount) * 100, 100);

    const completionPercentage = (completedCount / requiredCount) * 100;

    let completedPortion: number;
    let inProgressPortion: number;
    let plannedPortion: number;
    let suggestedPortion: number;
    if (engagedCount > 0) {
      completedPortion = (completedCount / engagedCount) * engagementProgress;
      inProgressPortion = (inProgressCount / engagedCount) * engagementProgress;
      plannedPortion = (plannedCount / engagedCount) * engagementProgress;
      suggestedPortion = (suggestedCount / engagedCount) * engagementProgress;
    } else {
      completedPortion = 0;
      inProgressPortion = 0;
      plannedPortion = 0;
      suggestedPortion = 0;
    }

    // W27: when an override covers this node's subtree, the backend supplies an
    // effective requirement-completion. Render a single "via override" fill and
    // label, while keeping the raw count breakdown below (honest dual-status).
    const effective = requirement?.effective_progress_percentage;
    const hasEffective = typeof effective === 'number';
    const effectiveFill = hasEffective ? Math.min(Math.max(effective, 0), 100) : 0;

    return (
      <div className="w-full">
        {hasEffective ? (
          <div className="flex h-2 bg-surface-sunken rounded-full overflow-hidden">
            <div
              className="bg-accent transition-all duration-300"
              style={{ width: `${effectiveFill}%` }}
              title="Effective completion includes a student-recorded override"
            />
          </div>
        ) : (
          <div className="flex h-2 bg-surface-sunken rounded-full overflow-hidden">
          {completedPortion > 0 && (
            <div
              className="bg-green-500 transition-all duration-300"
              style={{ width: `${completedPortion}%` }}
              title={`${completedCount} completed`}
            />
          )}

          {inProgressPortion > 0 && (
            <div
              className="bg-blue-500 transition-all duration-300"
              style={{ width: `${inProgressPortion}%` }}
              title={`${inProgressCount} in progress`}
            />
          )}

          {plannedPortion > 0 && (
            <div
              className="bg-yellow-500 transition-all duration-300"
              style={{ width: `${plannedPortion}%` }}
              title={`${plannedCount} planned`}
            />
          )}

          {suggestedPortion > 0 && (
            <div
              className="bg-status-suggested/50 transition-all duration-300"
              style={{ width: `${suggestedPortion}%` }}
              title={`${suggestedCount} suggested`}
            />
          )}
          </div>
        )}

        {showText && (
          <div className="text-xs text-gray-600 mt-1">
            {hasEffective && (
              <span className="text-accent font-medium">
                {effectiveFill >= 100
                  ? 'Satisfied via override'
                  : `${Math.round(effectiveFill)}% incl. override`}
                {' · '}
              </span>
            )}
            {completedCount}
            {unit} completed
            {inProgressCount > 0 && `, ${inProgressCount}${unit} in progress`}
            {plannedCount > 0 && `, ${plannedCount}${unit} planned`}
            {suggestedCount > 0 && `, ${suggestedCount}${unit} suggested`}
            {` of ${requiredCount}${unit} required`}
            {completedCount > 0 && ` (${Math.round(completionPercentage)}% completed)`}
          </div>
        )}
      </div>
    );
  }
);

RequirementProgressBar.displayName = 'RequirementProgressBar';

export default RequirementProgressBar;
